import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  timestamp: number
  read: boolean
}

interface AppState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  notifications: Notification[]
  isOnline: boolean
  selectedAgencyProviderId: string | null
  providerWorkWith: string | null
  providerHasAgency: boolean
}

const initialState: AppState = {
  sidebarOpen: true,
  theme: 'light',
  notifications: [],
  isOnline: navigator.onLine,
  selectedAgencyProviderId: null,
  providerWorkWith: null,
  providerHasAgency: false,
}

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    toggleTheme: state => {
      state.theme = state.theme === 'light' ? 'dark' : 'light'
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload
    },
    setSelectedAgencyProviderId: (state, action: PayloadAction<string | null>) => {
      state.selectedAgencyProviderId = action.payload
    },
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'timestamp' | 'read'>>
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: Date.now(),
        read: false,
      }
      state.notifications.unshift(notification)
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        n => n.id !== action.payload
      )
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        n => n.id === action.payload
      )
      if (notification) {
        notification.read = true
      }
    },
    clearAllNotifications: state => {
      state.notifications = []
    },
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload
    },
    setProviderProfileInfo: (state, action: PayloadAction<{ providerWorkWith: string | null; providerHasAgency: boolean }>) => {
      state.providerWorkWith = action.payload.providerWorkWith
      state.providerHasAgency = action.payload.providerHasAgency
    },
  },
})

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleTheme,
  setTheme,
  setSelectedAgencyProviderId,
  addNotification,
  removeNotification,
  markNotificationAsRead,
  clearAllNotifications,
  setOnlineStatus,
  setProviderProfileInfo,
} = appSlice.actions

export default appSlice.reducer

// Listen for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    // This will be handled by a middleware or component
  })

  window.addEventListener('offline', () => {
    // This will be handled by a middleware or component
  })
}
